import { useEffect, useId, useRef, useState } from 'react';

/** 工信部要求的 AI 生成内容标识：洋红色小号角标，无背景。 */
export const AI_GENERATED_MARK_COLOR = '#D5006D';
/** 总开关：角标组件本身是否渲染。 */
export const AI_GENERATED_CONTENT_BADGE_ENABLED = true;
/**
 * 知识颗粒详情 / 副券问答文末 AI 角标（小米等商店审核要求展示）。
 */
export const AI_GENERATED_MARK_ON_KP_QA_ENABLED = true;
/**
 * 品题画布卡片（原始 / 衍生 / 浮窗）文末 AI 角标。
 */
export const AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED = true;
/**
 * 暂时关闭：品题浓缩详情「隐性能力」条目 AI 角标。
 * 调用点与组件保留，改回 true 即可恢复。
 */
export const AI_GENERATED_MARK_ON_IPTTQ_IMPLICIT_ENABLED = false;

export const AI_GENERATED_CONTENT_NOTICE = 'AI 生成内容，请谨慎甄别使用。';

type AiGeneratedInlineMarkProps = {
  baseFontSize?: number;
  className?: string;
};

/** 文本末尾洋红色「AI」角标（更小、无背景）；hover / 点击弹出提示气泡。 */
export function AiGeneratedInlineMark({
  baseFontSize = 14,
  className = '',
}: AiGeneratedInlineMarkProps) {
  const fontSize = Math.max(9, Math.round(baseFontSize * 0.62));
  const tipId = useId();
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!AI_GENERATED_CONTENT_BADGE_ENABLED) return null;

  return (
    <span
      ref={rootRef}
      className={`relative ml-0.5 inline-block align-super ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={AI_GENERATED_CONTENT_NOTICE}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className="inline cursor-help border-0 bg-transparent p-0 font-bold leading-none"
        style={{ color: AI_GENERATED_MARK_COLOR, fontSize }}
      >
        AI
      </button>
      {open ? (
        <span
          id={tipId}
          role="tooltip"
          className={
            'absolute bottom-[calc(100%+6px)] left-1/2 z-50 w-max max-w-[220px] -translate-x-1/2 '
            + 'rounded-md border border-border bg-surface px-2 py-1.5 text-left text-[11px] '
            + 'font-medium leading-snug text-ink shadow-lg'
          }
        >
          {AI_GENERATED_CONTENT_NOTICE}
          <span
            aria-hidden
            className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[5px] border-x-transparent border-t-border"
          />
        </span>
      ) : null}
    </span>
  );
}

type AiGeneratedImageCornerMarkProps = {
  className?: string;
};

/** 图片右下角洋红色「AI」角标。 */
export function AiGeneratedImageCornerMark({ className = '' }: AiGeneratedImageCornerMarkProps) {
  if (!AI_GENERATED_CONTENT_BADGE_ENABLED) return null;
  return (
    <span
      title={AI_GENERATED_CONTENT_NOTICE}
      aria-hidden
      className={`pointer-events-none absolute bottom-1 right-1.5 z-[6] text-[11px] font-extrabold tracking-wide drop-shadow-[0_0_2px_rgba(255,255,255,0.9)] ${className}`.trim()}
      style={{ color: AI_GENERATED_MARK_COLOR }}
    >
      AI
    </span>
  );
}
